/**
 * SengiLabs Workflow Types for Frontend
 * Matches backend types for seamless integration
 */

// Core IO Types - matches backend IOType enum
export enum IOType {
  // Basic Types
  STRING = "STRING",
  INT = "INT", 
  FLOAT = "FLOAT",
  BOOLEAN = "BOOLEAN",
  
  // Image/Media Types
  IMAGE = "IMAGE",
  MASK = "MASK",
  LATENT = "LATENT",
  
  // Model Types
  MODEL = "MODEL",
  VAE = "VAE",
  CLIP = "CLIP",
  CONDITIONING = "CONDITIONING",
  
  // Special Types
  ANY = "*"
}

// Input type definition
export interface InputTypeDefinition {
  required?: Record<string, [IOType | string, InputOptions?]>;
  optional?: Record<string, [IOType | string, InputOptions?]>;
  hidden?: Record<string, string>;
}

// Input options for widget configuration
export interface InputOptions {
  default?: any;
  min?: number;
  max?: number;
  step?: number;
  multiline?: boolean;
  tooltip?: string;
  forceInput?: boolean;
  choices?: string[] | number[];
  [key: string]: any;
}

// Node link structure [node_id, output_index]
export type NodeLink = [string, number];

// Node input can be direct value or link to another node
export type NodeInputValue = any | NodeLink;

// Workflow node definition
export interface WorkflowNode {
  inputs: Record<string, NodeInputValue>;
  class_type: string;
  _meta?: {
    title?: string;
    color?: string;
    bgcolor?: string;
  };
}

// Complete workflow definition
export interface Workflow {
  [nodeId: string]: WorkflowNode;
}

// Extended workflow with metadata (for toolbar and UI)
export interface WorkflowGraph {
  meta: {
    name: string;
    version: string;
    created: string;
    description?: string;
  };
  nodes: Record<string, any>;
  connections: Connection[];
  metadata?: {
    lastExecuted?: string;
  };
}

// Canvas position
export interface Position {
  x: number;
  y: number;
}

// Canvas size
export interface Size {
  width: number;
  height: number;
}

// Node bounds
export interface Bounds extends Position, Size {}

// Socket definition
export interface Socket {
  id: string;
  type: 'input' | 'output';
  dataType: IOType | string;
  label: string;
  name?: string; // For backward compatibility
  position: Position;
  connected: boolean;
  connections: Connection[];
}

// Connection between nodes
export interface Connection {
  id: string;
  sourceNodeId: string;
  sourceSocketId: string;
  targetNodeId: string;
  targetSocketId: string;
  points: Position[];
}

// Canvas node representation
export interface CanvasNode {
  id: string;
  position: Position;
  size: Size;
  classType: string;
  title: string;
  inputs: Socket[];
  outputs: Socket[];
  selected: boolean;
  dragging: boolean;
  color?: string;
  bgcolor?: string;
  data: Record<string, any>;
  // Extended display modes
  displayMode?: 'normal' | 'expanded' | 'minimized' | 'maximized';
  collapsed?: boolean;
}

// Node definition from API
export interface NodeDefinition {
  input: InputTypeDefinition;
  output: (IOType | string)[];
  output_is_list: boolean[];
  output_name: string[];
  name: string;
  display_name: string;
  description: string;
  category: string;
  output_node: boolean;
  deprecated: boolean;
  experimental: boolean;
}

// Object info from API
export interface ObjectInfo {
  [className: string]: NodeDefinition;
}

// Canvas state
export interface CanvasState {
  nodes: Map<string, CanvasNode>;
  connections: Map<string, Connection>;
  selection: Set<string>;
  viewport: {
    x: number;
    y: number;
    scale: number;
  };
  grid: {
    size: number;
    visible: boolean;
    snap: boolean;
  };
}

// Canvas interaction mode
export enum InteractionMode {
  SELECT = 'select',
  PAN = 'pan',
  CONNECT = 'connect',
  ZOOM = 'zoom'
}

// Drag state
export interface DragState {
  isDragging: boolean;
  dragType: 'node' | 'selection' | 'connection' | 'canvas';
  startPosition: Position;
  currentPosition: Position;
  draggedItems: Set<string>;
}

// Selection rectangle
export interface SelectionRect {
  start: Position;
  end: Position;
  active: boolean;
}

// Context menu item
export interface ContextMenuItem {
  id: string;
  label: string;
  icon?: string;
  action: () => void;
  disabled?: boolean;
  separator?: boolean;
  submenu?: ContextMenuItem[];
}

// Keyboard shortcut
export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  action: () => void;
  description: string;
}

// History state for undo/redo
export interface HistoryState {
  nodes: Map<string, CanvasNode>;
  connections: Map<string, Connection>;
  universalNodes: Record<string, any>; // UniversalWorkflowNode from node-definitions
  timestamp: number;
  description: string;
}

// Utility type checkers
export function isNodeLink(value: any): value is NodeLink {
  return Array.isArray(value) && value.length === 2 && 
         typeof value[0] === 'string' && typeof value[1] === 'number';
}

export function isValidIOType(value: string): value is IOType {
  return Object.values(IOType).includes(value as IOType);
}

// Color utilities for node types
export const NODE_COLORS: Record<string, { color: string; bgcolor: string }> = {
  'loaders': { color: '#4CAF50', bgcolor: '#E8F5E8' },
  'conditioning': { color: '#2196F3', bgcolor: '#E3F2FD' },
  'sampling': { color: '#FF9800', bgcolor: '#FFF3E0' },
  'latent': { color: '#9C27B0', bgcolor: '#F3E5F5' },
  'image': { color: '#00BCD4', bgcolor: '#E0F2F1' },
  'model': { color: '#795548', bgcolor: '#EFEBE9' },
  'advanced': { color: '#607D8B', bgcolor: '#ECEFF1' },
  'default': { color: '#757575', bgcolor: '#F5F5F5' }
};

// Get node color based on category
export function getNodeColor(category: string): { color: string; bgcolor: string } {
  return NODE_COLORS[category] || NODE_COLORS.default;
}